import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { appState } from '@good-season/platform';
import { Button } from '@good-season/ui';

import { api } from '../api/client.js';
import type { WaitingUpdate } from './update-store.js';
import {
  dismissWaitingUpdate,
  getServerUpdateSnapshot,
  getUpdateSnapshot,
  subscribeToUpdates,
} from './update-store.js';

/**
 * "A new version is ready", offered rather than applied.
 *
 * ## Not a Dialog, and not a Toast
 *
 * Not a Dialog, because a Dialog is modal and below 640px renders as a Sheet
 * that covers the screen. An update is never urgent enough to take the screen
 * away from somebody, and taking it away mid-game is the exact failure the
 * prompt policy exists to prevent.
 *
 * Not a Toast, because a Toast carries no action and auto-dismisses. An offer
 * that disappears on a timer is an offer nobody accepts, and extending the
 * shared Toast for one caller would change the design system for a product
 * decision that belongs to this screen.
 *
 * So it is a bar: it sits above the content, it does not trap focus, and it
 * waits.
 *
 * ## Why "Not now" leaves it installed
 *
 * Dismissing does NOT discard the update. The worker stays in `waiting` and the
 * offer returns on the next load. `registerServiceWorker` states the policy an
 * update must obey: it must never be applied to a running scorebook, because
 * that can happen in the third quarter to a volunteer holding an unsynced event
 * queue, and the cost is a game's data in front of parents. So the scorebook's
 * answer is always "not now" until the game is final, and the version they are
 * running keeps working until they choose otherwise.
 *
 * `apply` reloads the page. Anything unsaved goes with it, which is precisely
 * why it is a button somebody presses rather than something that happens.
 */
export function UpdatePrompt() {
  // The selected team lives only in the query string; carry it to What's new.
  const [searchParams] = useSearchParams();
  const team = searchParams.get('team');
  const changelogTo =
    team === null || team === ''
      ? '/changelog'
      : { pathname: '/changelog', search: `?${new URLSearchParams({ team }).toString()}` };
  /*
   * One snapshot carrying both, because `useSyncExternalStore` compares with
   * `Object.is`. Reading the hold separately during render meant taking one
   * produced an identical snapshot, React correctly skipped the re-render, and
   * the Update button survived a hold that existed to remove it.
   */
  const { waiting, holdReason } = useSyncExternalStore(
    subscribeToUpdates,
    getUpdateSnapshot,
    getServerUpdateSnapshot,
  );

  /*
   * WHICH update is being applied, not merely that one is.
   *
   * `applyUpdate` is async and the reload arrives later, from the `controlling`
   * event, after the new worker activates. With `cleanupOutdatedCaches` on and a
   * precache to swap, that is not instant on a cold phone. Without any in-flight
   * state the bar sat unchanged with a live button through the whole gap, so the
   * only feedback for pressing Update was that nothing happened, which is what
   * makes somebody press it again.
   *
   * The first version of this was a plain boolean that nothing ever cleared, and
   * the delta pass was right that it made a dead end. Three sequences reached
   * it: the reload never arriving (`messageSkipWaiting` is a silent no-op when
   * the waiting worker is already gone, so `apply` resolves and nothing
   * happens), a SECOND version being announced while the first was applying, and
   * `apply` throwing. In all three the bar latched on "Updating." forever, and
   * because that version also removed both buttons there was no way out. This
   * component sits above the router and never unmounts, so "forever" meant the
   * rest of the session on every screen.
   *
   * Holding the update object rather than a flag fixes the second sequence by
   * construction: a new `waiting` is a different object, so `applying` is false
   * again and the offer comes back on its own.
   */
  const [applyingFor, setApplyingFor] = useState<WaitingUpdate | null>(null);
  const applying = waiting !== null && applyingFor === waiting;

  /*
   * WHICH update the person was signed in for, asked of the server the moment
   * an update is announced. The link to What's new is for signed-in people
   * only (the change log is not public), and this component sits outside
   * every screen, so nothing nearer knows. Any failure, including no network,
   * reads as signed out: the bar then shows the plain offer, which is always
   * right to show.
   */
  const [signedInFor, setSignedInFor] = useState<WaitingUpdate | null>(null);
  const signedIn = waiting !== null && signedInFor === waiting;

  /*
   * Asked again whenever the route changes or the app comes back to the
   * foreground, because this component outlives sign-in and sign-out: an
   * update announced on /signin must gain the link after sign-in, and a shown
   * link must go after sign-out. A failed probe clears it, so the plain offer
   * (and Update) is what remains.
   */
  const { pathname } = useLocation();
  const [foregrounded, setForegrounded] = useState(0);
  useEffect(
    () =>
      appState.onChange((state) => {
        if (state === 'active') setForegrounded((n) => n + 1);
      }),
    [],
  );

  useEffect(() => {
    if (waiting === null) return;
    let cancelled = false;
    api('/api/auth/me')
      .then(() => {
        if (!cancelled) setSignedInFor(waiting);
      })
      .catch(() => {
        if (!cancelled) setSignedInFor(null);
      });
    return () => {
      cancelled = true;
    };
  }, [waiting, pathname, foregrounded]);

  /*
   * Nothing holds today. The scorebook will, and the point is that it does not
   * have to find this file and remember to add a check: it takes a hold and
   * this refuses on its own. Raised as a P1 by the cross-model pass, which was
   * right that the button was unconditional even though the thing it could
   * destroy does not exist yet.
   */
  const held = holdReason;

  return (
    /*
     * `role="status"` with `aria-live="polite"`, not `alert`. It is news, not an
     * emergency: polite waits for a screen reader to finish its sentence, and
     * `alert` would interrupt somebody mid-roster.
     *
     * The live region is mounted from the FIRST render and empty, rather than
     * appearing with its content already inside it. A region inserted into the
     * document already populated is commonly not announced at all, because
     * there was no change to observe: the change has to happen to a region that
     * is already being watched. Unstyled and empty it is a zero-height div in
     * normal flow, so it costs nothing while there is no news.
     */
    <div className="updatePrompt__live" role="status" aria-live="polite">
      {waiting === null ? null : (
        <div className="updatePrompt">
          <div className="updatePrompt__text">
            <p className="updatePrompt__title">
              {signedIn ? 'New version ready.' : 'A new version is ready.'}
            </p>
            <p className="updatePrompt__body">
              {held === null
                ? 'Updating reloads the app. Anything you are part way through is lost.'
                : held}
            </p>
          </div>
          <div className="updatePrompt__actions">
            {/*
              "Not now" is ALWAYS here, including while applying.

              It was removed alongside Update in the first version of the
              in-flight state, on the reasoning that there is nothing left to
              offer once the press has happened. That reasoning is wrong when
              the reload does not arrive, which is exactly the case the
              in-flight state exists for: it left the bar with no control of any
              kind. Dismissing does not cancel a reload that is genuinely
              coming, and if one is not coming it is the only way out.
            */}
            <Button
              tone="ghost"
              onClick={() => {
                dismissWaitingUpdate();
              }}
            >
              Not now
            </Button>
            {/*
              A router link, not a button and not a new tab: it opens the
              change log in this app and applies nothing. Absent while the
              reload is coming, because there is nothing to read first.
            */}
            {signedIn && !applying ? (
              <Link to={changelogTo} className="updatePrompt__link">
                See what changed
              </Link>
            ) : null}
            {applying ? (
              /*
               * Text, not a disabled button. The press already happened, so a
               * greyed-out Update would only invite a second press.
               */
              <p className="updatePrompt__applying">Updating.</p>
            ) : held === null ? (
              /*
                Absent while held, not disabled. A disabled button is a thing
                you can see and cannot use, which invites pressing it; the
                reason above says what has to finish first, and the button
                returns when it does.
              */
              <Button
                onClick={() => {
                  setApplyingFor(waiting);
                  /*
                   * `await`, not a bare try/catch around the call.
                   *
                   * The first version of this caught only a SYNCHRONOUS throw,
                   * and `applyUpdate` is async, so the case it was written for
                   * went straight past it: the promise rejected, the bar stayed
                   * on "Updating." and the offer never came back. Graded P2 by
                   * the cross-model pass, which was right, and the comment sat
                   * here claiming the error was reported elsewhere. It was not.
                   * It was an unhandled rejection.
                   *
                   * Awaiting covers both: a synchronous throw becomes a rejected
                   * promise, and either way the offer returns so it can be tried
                   * again.
                   */
                  void (async () => {
                    try {
                      await waiting.apply();
                    } catch {
                      /*
                       * Clear only if this is STILL the update being applied.
                       *
                       * The previous round caught synchronously, inside the
                       * click handler, so the clear could only ever land on the
                       * update it had just set. Awaiting means the catch can
                       * arrive arbitrarily late, and an unconditional clear then
                       * reaches across versions: press Update on v1, the worker
                       * announces v2, press Update on v2, and v1's abandoned
                       * promise finally rejects and drops v2 out of "Updating."
                       * while its apply is genuinely in flight. That re-offers
                       * the button mid-apply, which is the exact "invites a
                       * second press" failure this state exists to prevent.
                       * Found by the delta pass over this round.
                       */
                      setApplyingFor((current) => (current === waiting ? null : current));
                    }
                  })();
                }}
              >
                Update
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
